import { z } from 'zod';

import { BooleanQueryParamSchema, IsoDateTimeSchema, UuidSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Package master (operator SKU catalog) shared schema (Phase B2).
 *
 * Sibling to the public-facing `website_marketing_itineraries` namespace.
 * Packages live in their own table and can seed an Itinerary when an agent
 * clones from a package in the editor (Phase B4/B5).
 *
 * Child editors (destinations / hotels / flights / inclusions / exclusions /
 * FAQs) ship in Phase B2.2; the detail response returns them as read-only
 * arrays so the admin can show legacy content immediately.
 */

export const PackageStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED']);
export type PackageStatus = z.infer<typeof PackageStatusSchema>;

/** Full month names stored on packages and shown in the admin month picker. */
export const PACKAGE_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;
export type PackageMonthName = (typeof PACKAGE_MONTH_NAMES)[number];

const PACKAGE_MONTH_ABBREV: Record<string, PackageMonthName> = {
  Jan: 'January',
  Feb: 'February',
  Mar: 'March',
  Apr: 'April',
  May: 'May',
  Jun: 'June',
  Jul: 'July',
  Aug: 'August',
  Sep: 'September',
  Sept: 'September',
  Oct: 'October',
  Nov: 'November',
  Dec: 'December',
};

/** Drop empty / legacy values; map short month codes to full names. Empty = any month. */
export const normalizePreferredMonths = (value: unknown): PackageMonthName[] => {
  const monthSet = new Set<string>(PACKAGE_MONTH_NAMES);
  if (!Array.isArray(value)) return [];
  const out: PackageMonthName[] = [];
  for (const raw of value) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const resolved = monthSet.has(trimmed)
      ? (trimmed as PackageMonthName)
      : PACKAGE_MONTH_ABBREV[trimmed];
    if (resolved && !out.includes(resolved)) out.push(resolved);
  }
  return out;
};

const PackagePreferredMonthsSchema = z.preprocess(
  (val) => normalizePreferredMonths(val),
  z.array(z.enum(PACKAGE_MONTH_NAMES)).max(12),
);

// ---- Child read-only shapes ----

export const PackageHotelOutSchema = z.object({
  id: UuidSchema,
  hotelId: UuidSchema.nullable(),
  cityName: z.string().nullable(),
  hotelName: z.string().nullable(),
  category: z.string().nullable(),
  nights: z.number().int().nullable(),
  occupancy: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type PackageHotelOut = z.infer<typeof PackageHotelOutSchema>;

export const PackageFlightOutSchema = z.object({
  id: UuidSchema,
  airlineId: UuidSchema.nullable(),
  sectorFrom: z.string().nullable(),
  sectorTo: z.string().nullable(),
  flightNumber: z.string().nullable(),
  departureDate: z.string().nullable(),
  departureTime: z.string().nullable(),
  arrivalDate: z.string().nullable(),
  arrivalTime: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type PackageFlightOut = z.infer<typeof PackageFlightOutSchema>;

export const PackageInclusionOutSchema = z.object({
  id: UuidSchema,
  label: z.string(),
  inclusionExclusionId: UuidSchema.nullable(),
  sortOrder: z.number().int(),
});
export type PackageInclusionOut = z.infer<typeof PackageInclusionOutSchema>;

export const PackageExclusionOutSchema = PackageInclusionOutSchema;
export type PackageExclusionOut = z.infer<typeof PackageExclusionOutSchema>;

export const PackageFaqOutSchema = z.object({
  id: UuidSchema,
  question: z.string(),
  answerHtml: z.string(),
  sortOrder: z.number().int(),
});
export type PackageFaqOut = z.infer<typeof PackageFaqOutSchema>;

export const PackageDayOutSchema = z.object({
  id: UuidSchema,
  dayIndex: z.number().int(),
  cityId: UuidSchema.nullable(),
  cityName: z.string().nullable(),
  town: z.string().nullable(),
  title: z.string().nullable(),
  subtitle: z.string().nullable(),
  mealPlan: z.string().nullable(),
  briefHtml: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type PackageDayOut = z.infer<typeof PackageDayOutSchema>;

export const PackageBatchStatusSchema = z.enum(['OPEN', 'CLOSED', 'FULL']);
export type PackageBatchStatus = z.infer<typeof PackageBatchStatusSchema>;

export const PackageVariantOutSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  price: z.string(),
  strikePrice: z.string().nullable(),
  description: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type PackageVariantOut = z.infer<typeof PackageVariantOutSchema>;

export const PackageBatchOutSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  departureDate: z.string(),
  returnDate: z.string().nullable(),
  capacity: z.number().int().nullable(),
  status: PackageBatchStatusSchema,
  sortOrder: z.number().int(),
  variants: z.array(PackageVariantOutSchema),
});
export type PackageBatchOut = z.infer<typeof PackageBatchOutSchema>;

export const PackageImageOutSchema = z.object({
  id: UuidSchema,
  mediaAssetId: UuidSchema,
  altText: z.string().nullable(),
  sortOrder: z.number().int(),
});
export type PackageImageOut = z.infer<typeof PackageImageOutSchema>;

export const PackageCollectionOutSchema = z.object({
  id: UuidSchema,
  marketingTagId: UuidSchema,
  name: z.string(),
  sortOrder: z.number().int(),
});
export type PackageCollectionOut = z.infer<typeof PackageCollectionOutSchema>;

export const PackageHeroInclusionOutSchema = z.object({
  id: UuidSchema,
  heroInclusionId: UuidSchema,
  name: z.string(),
  iconImageId: UuidSchema.nullable(),
  sortOrder: z.number().int(),
});
export type PackageHeroInclusionOut = z.infer<typeof PackageHeroInclusionOutSchema>;

// ---- Master list / detail ----

export const PackageListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  slug: z.string(),
  name: z.string(),
  shortDescription: z.string().nullable(),
  packageCategory: z.string().nullable(),
  packageTheme: z.string().nullable(),
  tripTypeId: UuidSchema.nullable(),
  isInternational: z.boolean(),
  /// Operator-set MICE flag - MICE packages feed the MICE page's
  /// "Destinations We Cover" destination picker.
  isMice: z.boolean(),
  preferredMonths: z.array(z.string()),
  numberOfNights: z.number().int(),
  numberOfDays: z.number().int(),
  /** Rupees (up to 2 decimals), serialised as a string to preserve precision. */
  price: z.string(),
  strikePrice: z.string().nullable(),
  status: PackageStatusSchema,
  rating: z.number().min(0).max(5).nullable(),
  createdAt: IsoDateTimeSchema,
});
export type PackageListItem = z.infer<typeof PackageListItemSchema>;

export const PackageListQuerySchema = PaginationQuerySchema.extend({
  status: PackageStatusSchema.optional(),
  packageCategory: z.string().trim().min(1).optional(),
  /**
   * One slug out of the pipe-separated `packageCategory` list
   * (`international|cruise-holidays`), unlike `packageCategory` above which
   * matches the whole string. `none` = no category at all.
   */
  categorySlug: z.string().trim().min(1).optional(),
  /** Filter to packages linked to this destination master (e.g. the landing-page
   *  "packages in Europe" picker). `none` = not linked to any live destination. */
  destinationId: z.union([UuidSchema, z.literal('none')]).optional(),
  /** Trip Types master id; `none` = trip type not set. */
  tripTypeId: z.union([UuidSchema, z.literal('none')]).optional(),
  // NB: string-aware boolean - z.coerce.boolean() treats "false" as truthy, so
  // ?isInternational=false (Domestic only) would wrongly return international.
  isInternational: BooleanQueryParamSchema.optional(),
  isMice: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type PackageListQuery = z.infer<typeof PackageListQuerySchema>;

export const PackageListResponseSchema = paginatedResponseSchema(PackageListItemSchema);
export type PackageListResponse = z.infer<typeof PackageListResponseSchema>;

/** One payment milestone on the public package page (label + value). */
export const PackagePaymentTermSchema = z.object({
  label: z.string().trim().min(1).max(200),
  value: z.string().trim().min(1).max(500),
});
export type PackagePaymentTerm = z.infer<typeof PackagePaymentTermSchema>;

export const PackagePaymentTermsSchema = z.array(PackagePaymentTermSchema).max(20);
export type PackagePaymentTerms = z.infer<typeof PackagePaymentTermsSchema>;

export const PackageDetailSchema = PackageListItemSchema.extend({
  overviewHtml: z.string().nullable(),
  destinationId: UuidSchema.nullable(),
  hotelCost: z.string().nullable(),
  flightCost: z.string().nullable(),
  visaInsuranceHtml: z.string().nullable(),
  paymentTerms: PackagePaymentTermsSchema,
  costSummaryHtml: z.string().nullable(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  metaKeywords: z.string().nullable(),
  mainImageId: UuidSchema.nullable(),
  bannerImageId: UuidSchema.nullable(),
  bannerVideoId: UuidSchema.nullable(),
  ogImageId: UuidSchema.nullable(),
  termsHtml: z.string().nullable(),
  cancellationPolicyHtml: z.string().nullable(),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
  hotels: z.array(PackageHotelOutSchema),
  flights: z.array(PackageFlightOutSchema),
  inclusions: z.array(PackageInclusionOutSchema),
  exclusions: z.array(PackageExclusionOutSchema),
  faqs: z.array(PackageFaqOutSchema),
  days: z.array(PackageDayOutSchema),
  batches: z.array(PackageBatchOutSchema),
  images: z.array(PackageImageOutSchema),
  collections: z.array(PackageCollectionOutSchema),
  heroInclusions: z.array(PackageHeroInclusionOutSchema),
});
export type PackageDetail = z.infer<typeof PackageDetailSchema>;

// ---- Create / update (master fields only; child editors in B2.2) ----

const MoneyStringSchema = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, 'Amount in rupees with up to 2 decimals (e.g. "170000.98")');

const PackageCoreFieldsSchema = z.object({
  slug: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'Lowercase letters, digits and hyphens'),
  name: z.string().trim().min(1).max(300),
  shortDescription: z.string().trim().max(500).nullable().optional(),
  overviewHtml: z.string().max(100_000).nullable().optional(),
  packageCategory: z.string().trim().max(120).nullable().optional(),
  packageTheme: z.string().trim().max(120).nullable().optional(),
  tripTypeId: UuidSchema.nullable().optional(),
  destinationId: UuidSchema.nullable().optional(),
  isInternational: z.boolean().optional(),
  isMice: z.boolean().optional(),
  preferredMonths: PackagePreferredMonthsSchema.optional(),
  numberOfNights: z.number().int().min(0).max(365),
  numberOfDays: z.number().int().min(1).max(365),
  hotelCost: MoneyStringSchema.nullable().optional(),
  flightCost: MoneyStringSchema.nullable().optional(),
  price: MoneyStringSchema,
  strikePrice: MoneyStringSchema.nullable().optional(),
  visaInsuranceHtml: z.string().max(100_000).nullable().optional(),
  paymentTerms: PackagePaymentTermsSchema.optional(),
  costSummaryHtml: z.string().max(100_000).nullable().optional(),
  status: PackageStatusSchema.optional(),
  rating: z.number().min(0).max(5).nullable().optional(),
  metaTitle: z.string().trim().max(300).nullable().optional(),
  metaDescription: z.string().trim().max(500).nullable().optional(),
  metaKeywords: z.string().trim().max(500).nullable().optional(),
  mainImageId: UuidSchema.nullable().optional(),
  bannerImageId: UuidSchema.nullable().optional(),
  bannerVideoId: UuidSchema.nullable().optional(),
  ogImageId: UuidSchema.nullable().optional(),
  termsHtml: z.string().max(100_000).nullable().optional(),
  cancellationPolicyHtml: z.string().max(100_000).nullable().optional(),
});

export const CreatePackageRequestSchema = PackageCoreFieldsSchema;
export type CreatePackageRequest = z.infer<typeof CreatePackageRequestSchema>;

export const UpdatePackageRequestSchema = PackageCoreFieldsSchema.partial();
export type UpdatePackageRequest = z.infer<typeof UpdatePackageRequestSchema>;

// ---- Bulk-set child lists (replace the whole list in one request) ----

export const SetPackageInclusionsRequestSchema = z
  .object({
    items: z.array(z.object({ inclusionExclusionId: UuidSchema })).max(200),
  })
  .refine((v) => new Set(v.items.map((i) => i.inclusionExclusionId)).size === v.items.length, {
    message: 'Duplicate inclusion id',
  });
export type SetPackageInclusionsRequest = z.infer<typeof SetPackageInclusionsRequestSchema>;

export const SetPackageExclusionsRequestSchema = z
  .object({
    items: z.array(z.object({ inclusionExclusionId: UuidSchema })).max(200),
  })
  .refine((v) => new Set(v.items.map((i) => i.inclusionExclusionId)).size === v.items.length, {
    message: 'Duplicate exclusion id',
  });
export type SetPackageExclusionsRequest = z.infer<typeof SetPackageExclusionsRequestSchema>;

export const SetPackageCollectionsRequestSchema = z
  .object({
    marketingTagIds: z.array(UuidSchema).max(50),
  })
  .refine((v) => new Set(v.marketingTagIds).size === v.marketingTagIds.length, {
    message: 'Duplicate collection id',
  });
export type SetPackageCollectionsRequest = z.infer<typeof SetPackageCollectionsRequestSchema>;

export const SetPackageHeroInclusionsRequestSchema = z
  .object({
    heroInclusionIds: z.array(UuidSchema).max(50),
  })
  .refine((v) => new Set(v.heroInclusionIds).size === v.heroInclusionIds.length, {
    message: 'Duplicate hero inclusion id',
  });
export type SetPackageHeroInclusionsRequest = z.infer<typeof SetPackageHeroInclusionsRequestSchema>;

// ---- Child write requests (per-row add/edit) ----

export const CreatePackageDayRequestSchema = z.object({
  dayIndex: z.number().int().min(1).max(365),
  cityId: UuidSchema.nullable().optional(),
  town: z.string().trim().max(160).nullable().optional(),
  title: z.string().trim().max(200).nullable().optional(),
  subtitle: z.string().trim().max(200).nullable().optional(),
  mealPlan: z.string().trim().max(120).nullable().optional(),
  briefHtml: z.string().max(100_000).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreatePackageDayRequest = z.infer<typeof CreatePackageDayRequestSchema>;
export const UpdatePackageDayRequestSchema = CreatePackageDayRequestSchema.partial();
export type UpdatePackageDayRequest = z.infer<typeof UpdatePackageDayRequestSchema>;

export const CreatePackageHotelRequestSchema = z.object({
  hotelId: UuidSchema.nullable().optional(),
  cityName: z.string().trim().max(160).nullable().optional(),
  hotelName: z.string().trim().max(200).nullable().optional(),
  category: z.string().trim().max(60).nullable().optional(),
  nights: z.number().int().min(0).max(365).nullable().optional(),
  occupancy: z.string().trim().max(120).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreatePackageHotelRequest = z.infer<typeof CreatePackageHotelRequestSchema>;
export const UpdatePackageHotelRequestSchema = CreatePackageHotelRequestSchema.partial();
export type UpdatePackageHotelRequest = z.infer<typeof UpdatePackageHotelRequestSchema>;

const DateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');

export const CreatePackageFlightRequestSchema = z.object({
  airlineId: UuidSchema.nullable().optional(),
  sectorFrom: z.string().trim().max(120).nullable().optional(),
  sectorTo: z.string().trim().max(120).nullable().optional(),
  flightNumber: z.string().trim().max(40).nullable().optional(),
  departureDate: DateStringSchema.nullable().optional(),
  departureTime: z.string().trim().max(20).nullable().optional(),
  arrivalDate: DateStringSchema.nullable().optional(),
  arrivalTime: z.string().trim().max(20).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreatePackageFlightRequest = z.infer<typeof CreatePackageFlightRequestSchema>;
export const UpdatePackageFlightRequestSchema = CreatePackageFlightRequestSchema.partial();
export type UpdatePackageFlightRequest = z.infer<typeof UpdatePackageFlightRequestSchema>;

export const CreatePackageFaqRequestSchema = z.object({
  question: z.string().trim().min(1).max(500),
  answerHtml: z.string().min(1).max(100_000),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreatePackageFaqRequest = z.infer<typeof CreatePackageFaqRequestSchema>;
export const UpdatePackageFaqRequestSchema = CreatePackageFaqRequestSchema.partial();
export type UpdatePackageFaqRequest = z.infer<typeof UpdatePackageFaqRequestSchema>;

export const CreatePackageImageRequestSchema = z.object({
  mediaAssetId: UuidSchema,
  altText: z.string().trim().max(300).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreatePackageImageRequest = z.infer<typeof CreatePackageImageRequestSchema>;
export const UpdatePackageImageRequestSchema = z.object({
  altText: z.string().trim().max(300).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type UpdatePackageImageRequest = z.infer<typeof UpdatePackageImageRequestSchema>;

export const CreatePackageVariantRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  price: MoneyStringSchema,
  strikePrice: MoneyStringSchema.nullable().optional(),
  description: z.string().trim().max(500).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreatePackageVariantRequest = z.infer<typeof CreatePackageVariantRequestSchema>;
export const UpdatePackageVariantRequestSchema = CreatePackageVariantRequestSchema.partial();
export type UpdatePackageVariantRequest = z.infer<typeof UpdatePackageVariantRequestSchema>;

export const CreatePackageBatchRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  departureDate: DateStringSchema,
  returnDate: DateStringSchema.nullable().optional(),
  capacity: z.number().int().min(1).max(10_000).nullable().optional(),
  status: PackageBatchStatusSchema.optional(),
  sortOrder: z.number().int().min(0).optional(),
});
export type CreatePackageBatchRequest = z.infer<typeof CreatePackageBatchRequestSchema>;
export const UpdatePackageBatchRequestSchema = CreatePackageBatchRequestSchema.partial();
export type UpdatePackageBatchRequest = z.infer<typeof UpdatePackageBatchRequestSchema>;
